import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Check, X, Clock, Calendar, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/appointment-requests`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (action) => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/appointment-requests/${selectedRequest.id}/respond?action=${action}&response_message=${encodeURIComponent(responseMessage)}`
      );
      
      setRequests(prev => prev.map(r => 
        r.id === selectedRequest.id 
          ? { ...r, status: action === 'accept' ? 'accepted' : 'rejected', artist_response: responseMessage }
          : r
      ));
      
      setRespondDialogOpen(false);
      setSelectedRequest(null);
      setResponseMessage('');
      toast.success(action === 'accept' ? '¡Cita confirmada!' : 'Solicitud rechazada');
    } catch (error) {
      console.error('Error responding to request:', error);
      toast.error('Error al responder la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const openRespondDialog = (request) => {
    setSelectedRequest(request);
    setRespondDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="text-xs uppercase tracking-wider text-yellow-400 bg-yellow-900/30 px-2 py-1">Pendiente</span>;
      case 'accepted':
        return <span className="text-xs uppercase tracking-wider text-emerald-400 bg-emerald-900/30 px-2 py-1">Aceptada</span>;
      case 'rejected':
        return <span className="text-xs uppercase tracking-wider text-rose-400 bg-rose-900/30 px-2 py-1">Rechazada</span>;
      default:
        return null;
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  if (user?.user_type !== 'artist') {
    // Client view - show their sent requests
    return (
      <div className="space-y-4" data-testid="client-requests">
        <h3 className="font-['Cinzel'] text-lg font-semibold text-white mb-4">Mis Solicitudes</h3>
        {loading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-24 bg-zinc-900 animate-pulse" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/30 border border-zinc-800">
            <Calendar className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No has enviado solicitudes de cita</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div 
                key={request.id}
                className="bg-zinc-900/50 border border-zinc-800 p-6"
                data-testid={`request-${request.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-medium text-white">{request.artist_name}</p>
                    <p className="text-sm text-zinc-500">
                      {format(new Date(request.preferred_date), "d 'de' MMMM", { locale: es })} a las {request.preferred_time}
                    </p>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
                {request.description && (
                  <p className="text-sm text-zinc-400 mb-3">{request.description}</p>
                )}
                {request.artist_response && (
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <p className="text-xs text-zinc-500 mb-1">Respuesta del artista:</p>
                    <p className="text-sm text-zinc-300">{request.artist_response}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Artist view
  return (
    <div className="space-y-8" data-testid="artist-requests">
      {/* Pending Requests */}
      <div>
        <h3 className="font-['Cinzel'] text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock size={18} className="text-yellow-400" />
          Solicitudes Pendientes ({pendingRequests.length})
        </h3>
        {loading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-32 bg-zinc-900 animate-pulse" />
            ))}
          </div>
        ) : pendingRequests.length === 0 ? (
          <div className="text-center py-8 bg-zinc-900/30 border border-zinc-800">
            <p className="text-zinc-500">No hay solicitudes pendientes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingRequests.map((request) => (
              <div 
                key={request.id}
                className="bg-zinc-900/50 border border-zinc-800 p-6"
                data-testid={`pending-request-${request.id}`}
              >
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12 border border-zinc-800">
                    <AvatarImage src={request.client_avatar} />
                    <AvatarFallback className="bg-zinc-800 text-zinc-400">
                      {request.client_name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-white">{request.client_name}</p>
                      <span className="text-xs text-zinc-600">
                        {formatDistanceToNow(new Date(request.created_at), { addSuffix: true, locale: es })}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-zinc-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {format(new Date(request.preferred_date), "d 'de' MMMM", { locale: es })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {request.preferred_time}
                      </span>
                    </div>
                    {request.description && (
                      <p className="text-sm text-zinc-500 mb-4">{request.description}</p>
                    )}
                    <div className="flex gap-3">
                      <Button
                        onClick={() => openRespondDialog(request)}
                        className="bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
                        data-testid={`respond-${request.id}`}
                      >
                        Responder
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Processed Requests */}
      {processedRequests.length > 0 && (
        <div>
          <h3 className="font-['Cinzel'] text-lg font-semibold text-white mb-4">
            Solicitudes Anteriores
          </h3>
          <div className="space-y-4">
            {processedRequests.map((request) => (
              <div 
                key={request.id}
                className="bg-zinc-900/30 border border-zinc-800/50 p-4"
                data-testid={`processed-request-${request.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 border border-zinc-800">
                      <AvatarImage src={request.client_avatar} />
                      <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xs">
                        {request.client_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm text-white">{request.client_name}</p>
                      <p className="text-xs text-zinc-600">
                        {format(new Date(request.preferred_date), "d MMM", { locale: es })} - {request.preferred_time}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Respond Dialog */}
      <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cinzel'] text-xl">
              Responder a {selectedRequest?.client_name}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="bg-zinc-800/50 p-4 rounded">
                <p className="text-sm text-zinc-400 mb-2">
                  <strong>Fecha solicitada:</strong> {format(new Date(selectedRequest.preferred_date), "EEEE d 'de' MMMM", { locale: es })}
                </p>
                <p className="text-sm text-zinc-400">
                  <strong>Hora:</strong> {selectedRequest.preferred_time}
                </p>
                {selectedRequest.description && (
                  <p className="text-sm text-zinc-400 mt-2">
                    <strong>Descripción:</strong> {selectedRequest.description}
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm text-zinc-500 mb-2">Mensaje para el cliente (opcional)</p>
                <Textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Ej: Perfecto, nos vemos el día acordado. / Lo siento, ese día no tengo disponibilidad..."
                  className="bg-transparent border-zinc-800 min-h-[80px]"
                  data-testid="response-message"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleRespond('accept')}
                  disabled={submitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-widest text-xs font-bold"
                  data-testid="accept-request-btn"
                >
                  <Check size={16} className="mr-2" />
                  Aceptar
                </Button>
                <Button
                  onClick={() => handleRespond('reject')}
                  disabled={submitting}
                  variant="outline"
                  className="flex-1 border-rose-800 text-rose-400 hover:bg-rose-900/20 uppercase tracking-widest text-xs"
                  data-testid="reject-request-btn"
                >
                  <X size={16} className="mr-2" />
                  Rechazar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentRequests;
